import React, { useState } from 'react';
import { FormControl, TextField } from '@mui/material';
import { Button } from 'components';
import {
  Favorite,
  CompareArrows,
  NavigateBefore,
  NavigateNext,
  ShoppingCart,
  FlashOn,
} from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';
import './ProductDetails.styled.scss';
import { TProductCustomField } from 'api/types';
import { TProductDetails } from './ProductDetails.types';
import { addItemToLocalCart } from 'store/localCart';
import { useAppDispatch } from 'store/store';
import { CustomRadioGroup } from './components/CustomRadioGroup/CustomRadioGroup';

export const ProductDetails: TProductDetails = ({ product }) => {
  const dispatch = useAppDispatch();
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [customFieldValues, setCustomFieldValues] = useState<
    Record<string, string>
  >({});

  const handleCustomFieldChange = (fieldName: string, value: string) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const images = product.images || [];
  const imageUrls = images
    .map((img) => (typeof img === 'string' ? img : URL.createObjectURL(img)))
    .filter(Boolean);

  const handleAddToCart = () => {
    dispatch(
      addItemToLocalCart({
        id: product.id,
        title: product.name,
        pricePerItem: product.price,
        mainImage: product.images[0],
        quantity: 1,
        values: customFieldValues,
      })
    );
  };
  return (
    <div className="product-details">
      <div className="product-details__content-wrapper">
        {/* Media / Image Slider */}
        <div className="product-details__media">
          {imageUrls.length > 0 ? (
            <>
              <div className="product-details__image-container">
                <Swiper
                  spaceBetween={10}
                  navigation={{
                    nextEl: '.swiper-button-next-custom',
                    prevEl: '.swiper-button-prev-custom',
                  }}
                  thumbs={{
                    swiper:
                      thumbsSwiper && !thumbsSwiper.destroyed
                        ? thumbsSwiper
                        : null,
                  }}
                  modules={[Navigation, Thumbs]}
                  className="product-details__main-swiper"
                >
                  {imageUrls.map((url, index) => (
                    <SwiperSlide key={index}>
                      <img
                        src={url}
                        alt={`${product.name} - ${index + 1}`}
                        className="product-details__image"
                      />
                    </SwiperSlide>
                  ))}
                  <div className="swiper-button-prev-custom">
                    <NavigateBefore />
                  </div>
                  <div className="swiper-button-next-custom">
                    <NavigateNext />
                  </div>
                </Swiper>
                <div className="product-details__features">
                  <div className="product-details__feature">
                    <span className="product-details__feature-icon">🧲</span>
                    <span>Strong Magnetic</span>
                  </div>
                  <div className="product-details__feature">
                    <span className="product-details__feature-icon">📱</span>
                    <span>Mag-safe Charging</span>
                  </div>
                  <div className="product-details__feature">
                    <span className="product-details__feature-icon">🎯</span>
                    <span>Auto Alignment</span>
                  </div>
                </div>
              </div>
              {imageUrls.length > 1 && (
                <Swiper
                  onSwiper={setThumbsSwiper}
                  spaceBetween={10}
                  slidesPerView={4}
                  freeMode={true}
                  watchSlidesProgress={true}
                  modules={[FreeMode, Thumbs]}
                  className="product-details__thumbs-swiper"
                >
                  {imageUrls.map((url, index) => (
                    <SwiperSlide key={index}>
                      <img
                        src={url}
                        alt={`${product.name} thumbnail - ${index + 1}`}
                        className="product-details__thumb-image"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </>
          ) : (
            <div className="product-details__image-container product-details__image-container--empty">
              <p>No images available</p>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="product-details__info">
          <p className="product-details__code">Код товару: {product.id}</p>

          <span className="product-details__availability-badge">
            в наявності
          </span>

          <div className="product-details__price">
            {product.newPrice ? (
              <>
                <span className="product-details__price-old">
                  {product.price} zl.
                </span>{' '}
                <span className="product-details__price-new">
                  {product.newPrice} zl.
                </span>
              </>
            ) : (
              <span>{product.price} zl.</span>
            )}
          </div>

          {/* Custom Fields */}
          {product.customFields && product.customFields.length > 0 && (
            <div className="product-details__custom-fields">
              <h4 className="product-details__custom-fields-title">
                Додаткові опції
              </h4>
              {product.customFields.map(
                (field: TProductCustomField, index: number) => (
                  <FormControl
                    fullWidth
                    key={index}
                    className="product-details__custom-field"
                  >
                    {field.type === 'string' || field.type === 'text' ? (
                      <TextField
                        label={field.name}
                        placeholder={field.placeholder}
                        value={customFieldValues[field.name] || ''}
                        onChange={(e) =>
                          handleCustomFieldChange(field.name, e.target.value)
                        }
                        inputProps={{
                          maxLength: field.maxLength,
                          minLength: field.minLength,
                        }}
                        required
                        fullWidth
                      />
                    ) : field.type === 'radio' || field.type === 'Radio' ? (
                      <CustomRadioGroup
                        productCustomField={field}
                        value={customFieldValues[field.name] || ''}
                        onChange={(value) =>
                          handleCustomFieldChange(field.name, value)
                        }
                      />
                    ) : null}
                  </FormControl>
                )
              )}
            </div>
          )}

          {/* Description */}
          {product.description && (
            <div className="product-details__description-inline">
              <h4>Опис товару</h4>
              <p>{product.description}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="product-details__button-group">
            <Button
              variant="contained"
              fullWidth
              className="product-details__add-to-cart"
              startIcon={<ShoppingCart />}
              onClick={handleAddToCart}
            >
              До кошика
            </Button>
            <Button
              variant="contained"
              fullWidth
              className="product-details__buy-now"
              startIcon={<FlashOn />}
            >
              Купити зараз
            </Button>
          </div>

          {/* Actions */}
          <div className="product-details__actions">
            <Button startIcon={<Favorite />}>В обране</Button>
            <Button startIcon={<CompareArrows />}>Порівняти</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
